<template>
	<AppModal id="mfa-configuration-modal" class="mfa-config-modal" @close="handleClose">
		<!-- Auth Method Choices -->
		<div id="mfa-method-choice" v-if="this.flowStage == this.$constants.MFA.FLOW_STAGES.METHOD_CHOICE">
			<div class="mfa-modal-header">
				<img src="images/icons/ic-lock.svg" class="mfa-title-icon" />
				<h3 class="text-bold mb-2">{{ modalTitle }}</h3>
				<p class="modal-pretext text-light">
					{{ modalPretext }}
					<a href="https://help.stan.store/article/408-2fa" class="cursor-pointer">Learn more</a>
				</p>
			</div>
			<div class="mfa-choice-container mt-3">
				<AppButton
					name="mfa-authenticator-config-button"
					class="mfa-choice-btn w-100 h-auto"
					size="large"
					outline
					data-target="#mfa-method-choice-modal"
					@click="showQRData"
				>
					<div class="mfa-choice-row">
						<span class="mfa-choice-icon">
							<img src="images/icons/ic-clock.svg" />
						</span>
						<span>
							<p class="h3 mb-1 font-semibold">Code from authenticator</p>
							<p class="mb-0 font-regular text-light">Generate a one-time code in your authenticator app</p>
						</span>
					</div>
				</AppButton>
				<AppButton
					v-if="false"
					name="mfa-phone-config-button"
					class="mfa-choice-btn w-100 h-auto"
					size="large"
					disabled
					outline
					data-target="#mfa-method-choice-modal"
					@click="showMFANotificationFlow"
				>
					<div class="mfa-choice-row">
						<span class="mfa-choice-icon">
							<img src="images/icons/ic-message.svg" />
						</span>
						<span>
							<p class="h3 mb-1 font-semibold">Text me a code</p>
							<p class="mb-0 font-regular">Add and verify your phone number</p>
						</span>
					</div>
				</AppButton>
			</div>
		</div>
		<div id="mfa-qr-data" class="text-center" v-if="this.flowStage == this.$constants.MFA.FLOW_STAGES.AUTHENTICATOR_APP">
			<div class="mfa-modal-header">
				<h3 class="text-bold mb-2">{{ modalTitle }}</h3>
				<p class="modal-pretext text-light">{{ modalPretext }}</p>
			</div>
			<!-- Show QR code and "Can't Scan Code?" button -->
			<div v-if="qrCodeView" class="mb-6">
				<div id="mfa-qr-code" class="setup-code">
					<QrcodeVue :value="this.totpURI" :size="size" level="H"></QrcodeVue>
				</div>
				<p class="inner-pretext text-light">
					Can't scan the code?
				</p>
				<AppButton name="mfa-cant-scan-code" class="w-100 pt-0" link @click="qrCodeView = false">
					View the setup key instead
				</AppButton>
			</div>
			<!-- Show secret key and "Show QR" button -->
			<div v-else class="mb-6">
				<div id="mfa-qr-secret" class="secret">
					<p>{{ this.secret }}</p>
				</div>
				<AppButton
					name="mfa-copy-setup-key"
					icon="images/icons/ic-copy.svg"
					class="w-100 mb-3"
					size="medium"
					outline
					data-target="#mfa-configuration-modal"
					@click="copyToClipboard(secret)"
				>
					Copy Setup Key
				</AppButton>
				<AppButton name="mfa-show-qr" class="w-100" size="medium" link @click="qrCodeView = true">
					Scan QR code instead
				</AppButton>
			</div>
			<p class="modal-pretext text-light">
				2. Enter the one-time code from your authenticator
			</p>
			<OTPInput
				:error="error"
				:warning="warning"
				:state-change-key="stateChangeKey"
				@resetErrors="resetWarningsAndErrors"
				submit-button-text="Continue"
				@submit="validateConfigOTP"
			/>
		</div>
		<div id="mfa-verify-code" class="text-center" v-if="this.flowStage == this.$constants.MFA.FLOW_STAGES.VERIFY_CODE">
			<div class="mfa-modal-header">
				<h3 class="text-bold mb-2">{{ modalTitle }}</h3>
				<p class="modal-pretext text-light">
					Enter the one-time code from <strong>{{ modalPretext }}</strong> to complete setup
				</p>
			</div>
			<OTPInput
				:error="error"
				:warning="warning"
				:state-change-key="stateChangeKey"
				@resetErrors="resetWarningsAndErrors"
				submit-button-text="Continue"
				@submit="validateConfigOTP"
			/>
		</div>
		<div id="mfa-recovery-codes" class="text-center" v-if="this.flowStage == this.$constants.MFA.FLOW_STAGES.SAVE_RECOVERY_CODES">
			<div class="mfa-modal-header">
				<h3 class="text-bold mb-2">{{ modalTitle }}</h3>
				<p class="modal-pretext text-light"><strong>You can only see this once ⚠️</strong> {{ modalPretext }}</p>
			</div>
			<div class="action-button-group">
				<div class="recovery-card">
					<img src="images/icons/ic-copy.svg" class="copy-icon" alt="Copy" @click="copyToClipboard('recovery')" />
					<div class="recovery-text">{{ this.recovery }}</div>
				</div>
				<div v-if="!isCopiedOrDownloaded">
					<p class="modal-pretext text-light">Download or copy your recovery code to continue</p>
					<AppButton
						name="mfa-copy-recovery-codes"
						class="float-left mfa-complete-buttons"
						outline
						size="medium"
						data-target="#mfa-configuration-modal"
						@click="copyToClipboard('recovery')"
					>
						Copy
					</AppButton>
					<AppButton
						name="mfa-download-recovery-codes"
						class="float-right mfa-complete-buttons"
						size="medium"
						data-target="#mfa-configuration-modal"
						@click="downloadRecoveryCodes"
					>
						Download
					</AppButton>
				</div>
				<div class="d-flex recovery-save-action-card" v-else>
					<div class="action-text">Recovery Code {{ this.recoverySaveAction }}!</div>
					<InlineSvg class="check-mark-icon" src="/images/icons/ic-check-circle.svg" />
				</div>
				<div>
					<AppButton
						name="mfa-saved-recovery-codes"
						class="w-100 mb-2"
						size="medium"
						data-target="#mfa-configuration-modal"
						@click="showName"
						:disabled="!isCopiedOrDownloaded"
					>
						Continue
					</AppButton>
				</div>
			</div>
		</div>
		<div
			id="mfa-name-configuration"
			class="d-flex flex-column text-center"
			v-if="this.flowStage == this.$constants.MFA.FLOW_STAGES.NAME_CONFIGURATION"
		>
			<div class="mfa-modal-header">
				<h3 class="text-bold">{{ modalTitle }}</h3>
				<p class="modal-pretext text-light">{{ modalPretext }}</p>
			</div>
			<AppInput class="form-group mt-3" :placeholder="$t('Authenticator app')" name="authenticator" v-model="authenticatorName" />
			<AppButton
				name="mfa-continue-to-dashboard-button"
				class="w-100 float-right mfa-complete-buttons"
				data-target="#mfa-configuration-modal"
				@click="nameAndEnableConfiguration"
				type="submit"
				:loading="finishSetuploading"
				:disabled="!authenticatorName.trim() || finishSetuploading"
			>
				Finish Setup
			</AppButton>
		</div>
		<div id="mfa-complete-setup" class="text-center" v-if="this.flowStage == this.$constants.MFA.FLOW_STAGES.COMPLETE_CONFIGURATION">
			<div class="mfa-modal-header">
				<img src="images/icons/ic-check-circle-purple.svg" class="mfa-title-icon" />
				<h3 class="text-bold">{{ modalTitle }}</h3>
				<h3 class="text-bold mb-2">Your account is now much safer</h3>
				<p class="modal-pretext text-light">{{ modalPretext }}</p>
			</div>
			<AppButton
				v-if="false"
				name="mfa-add-another-method-button"
				class="float-left mfa-complete-buttons"
				outline
				data-target="#mfa-configuration-modal"
			>
				Add another method
			</AppButton>
			<AppButton
				name="mfa-continue-to-dashboard-button"
				class="w-100 float-right mfa-complete-buttons"
				data-target="#mfa-configuration-modal"
				@click="completeMFAConfiguration"
			>
				Awesome!
			</AppButton>
		</div>
		<div id="mfa-restore-setup" class="text-center" v-if="this.flowStage == this.$constants.MFA.FLOW_STAGES.RESTORE_CONFIGURATION">
			<div class="mfa-modal-header">
				<h3 class="text-bold mb-2">{{ modalTitle }}</h3>
				<p class="modal-pretext text-light mb-4">{{ modalPretext }}</p>
			</div>
			<AppButton
				name="mfa-restore-config-button"
				class="w-100 mb-2 float-right mfa-complete-buttons"
				data-target="#mfa-configuration-modal"
				@click="restoreMFAConfiguration"
			>
				Secure Your Account
			</AppButton>
		</div>
	</AppModal>
</template>
<script>
	/* eslint-disable import/extensions */
	import { configSettings } from '~/plugins/config-settings.js'
	import axios from 'axios'
	import QrcodeVue from 'qrcode.vue'
	import helper from '~/global_helper/helpers.js'
	import CONSTANTS from '../../global_helper/constants'

	export default {
		props: {
			status: { type: Number, default: 0 },
			totpURI: { type: String, default: '' },
			secret: { type: String, default: '' },
			nextRoute: { type: String, default: '' },
			source: { type: String, default: '' },
			flowStageStart: { type: Number, default: 0 },
		},
		components: {
			QrcodeVue,
		},
		data() {
			return {
				recovery: '',
				configId: undefined,
				ajaxWarnings: {},
				ajaxError: {
					message: '',
					status: null,
					errors: {},
				},
				form: {},
				loading: false,
				stateChangeKey: 0,
				size: 155,
				ttlDays: CONSTANTS.MFA.DEFAULT_TTL_DAYS,
				flowStage: this.flowStageStart,
				qrCodeView: true,
				authenticatorName: '',
				recoverySaveAction: '',
				finishSetuploading: false,
			}
		},
		computed: {
			modalTitle() {
				let titleText = ''
				if (this.flowStage === this.$constants.MFA.FLOW_STAGES.METHOD_CHOICE) {
					if (this.source === 'toggle') {
						// this.showMFAMethodChoice()
						titleText = 'Turn on two-step verification'
					} else if (this.source === 'button') {
						titleText = 'Add two-step verification method'
					}
				} else if (this.flowStage === this.$constants.MFA.FLOW_STAGES.AUTHENTICATOR_APP) {
					if (this.qrCodeView) {
						titleText = 'Scan code in authenticator app'
					} else {
						titleText = 'Add using your setup key'
					}
				} else if (this.flowStage === this.$constants.MFA.FLOW_STAGES.VERIFY_CODE) {
					titleText = 'Verify Code'
				} else if (this.flowStage === this.$constants.MFA.FLOW_STAGES.SAVE_RECOVERY_CODES) {
					titleText = 'Save your recovery code'
				} else if (this.flowStage === this.$constants.MFA.FLOW_STAGES.NAME_CONFIGURATION) {
					titleText = 'Name your 2FA method'
				} else if (this.flowStage === this.$constants.MFA.FLOW_STAGES.COMPLETE_CONFIGURATION) {
					titleText = '2FA is ON 🎉'
				} else if (this.flowStage === this.$constants.MFA.FLOW_STAGES.RESTORE_CONFIGURATION) {
					titleText = 'Restore multi-factor authentication'
				} else {
					titleText = 'Configure authenticator app'
				}
				return titleText
			},
			modalPretext() {
				let pretext = ''
				if (this.flowStage === this.$constants.MFA.FLOW_STAGES.METHOD_CHOICE) {
					if (this.source === 'toggle') {
						pretext = 'Verify your identity after entering your password with a verification code from an authenticator app. '
					} else if (this.source === 'button') {
						pretext = 'Two-step verification is already enabled. Change how you verify your identity after entering your password. '
					}
				} else if (this.flowStage === this.$constants.MFA.FLOW_STAGES.AUTHENTICATOR_APP) {
					if (this.qrCodeView) {
						pretext = '1. Scan the QR code to generate a unique code in your authenticator app'
					} else {
						pretext = '1. Copy the secret key below and paste it into your authenticator app'
					}
				} else if (this.flowStage === this.$constants.MFA.FLOW_STAGES.VERIFY_CODE) {
					pretext = this.authenticatorName.trim() === '' ? 'authenticator app' : this.authenticatorName.trim()
				} else if (this.flowStage === this.$constants.MFA.FLOW_STAGES.SAVE_RECOVERY_CODES) {
					pretext = 'Be sure to keep this safe to avoid getting locked out of your account'
				} else if (this.flowStage === this.$constants.MFA.FLOW_STAGES.NAME_CONFIGURATION) {
					pretext = 'This helps you recognize it later. Use the name of the app you used, like Google Authenticator or Authy'
				} else if (this.flowStage === this.$constants.MFA.FLOW_STAGES.RESTORE_CONFIGURATION) {
					pretext = 'You signed in with a backup code, your MFA was removed. Set it up again to keep your account protected.'
				} else if (this.flowStage === this.$constants.MFA.FLOW_STAGES.COMPLETE_CONFIGURATION) {
					pretext =
						'From now on, you’ll need to enter a verification code whenever we don’t recognize your device. To keep your account secure, you’ve been logged out of all other devices.'
				}
				return pretext
			},
			error() {
				return (this.ajaxError && this.ajaxError.errors && this.ajaxError.errors.otp) || []
			},
			warning() {
				return this.ajaxWarnings && this.ajaxWarnings.otp
			},
			isCopiedOrDownloaded() {
				return this.recoverySaveAction !== ''
			},
			loadingStatus() {
				return this.loading
			},
		},
		methods: {
			showMFANotificationFlow() {},
			showQRData() {
				this.flowStage = CONSTANTS.MFA.FLOW_STAGES.AUTHENTICATOR_APP
				this.qrCodeView = true
			},
			showMFAMethodChoice() {
				this.flowStage = CONSTANTS.MFA.FLOW_STAGES.METHOD_CHOICE
			},
			showCodeInputData() {
				this.flowStage = CONSTANTS.MFA.FLOW_STAGES.VERIFY_CODE
			},
			showRecoveryText() {
				this.flowStage = CONSTANTS.MFA.FLOW_STAGES.SAVE_RECOVERY_CODES
			},
			showCongratulations() {
				this.flowStage = CONSTANTS.MFA.FLOW_STAGES.COMPLETE_CONFIGURATION
			},
			showName() {
				this.flowStage = CONSTANTS.MFA.FLOW_STAGES.NAME_CONFIGURATION
			},
			nameAndEnableConfiguration() {
				this.finishSetuploading = true
				this.stateChangeKey += 1
				const configId = this.configId || this.nextRoute.split('id=')[1]
				const errorObject = {
					status: 'error',
					message: this.$t('The given data was invalid.'),
					errors: {},
				}
				this.stateChangeKey += 1

				Object.assign(this.ajaxError, errorObject)

				if (configId) {
					const enablePayload = {
						name: this.authenticatorName.trim(), // Add the name to payload
					}
					axios
						.post(`/v1/mfa/config/${configId}/enable`, enablePayload, {
							baseURL: configSettings.authApiUrl,
						})
						.then(() => {
							this.showCongratulations()
						})
						.catch(error => {
							this.$logError(error.response.data.message)
							Object.assign(errorObject.errors, {
								name: [this.$t(error.response.data.message)],
							})
							this.ajaxError = { ...errorObject }
							this.stateChangeKey += 1
						})
						.finally(() => {
							this.finishSetuploading = false
						})
				}
			},
			completeMFAConfiguration() {
				this.recoverySaveAction = ''
				this.resetWarningsAndErrors()
				// bubble the close event upwards
				this.$emit('close')

				$('#mfa-configuration-modal').modal('hide')

				// if we are coming from the enforced MFA, route to dashboard.
				if (this.$route.fullPath !== '/account_settings/?tab=security') {
					this.$router.push({ path: 'dashboard' })
				} else {
					// notify session to logout other sessions
					this.$emit('logoutOtherSessions')
				}
			},
			restoreMFAConfiguration() {
				// bubble the close event upwards
				this.$emit('close')

				$('#mfa-configuration-modal').modal('hide')

				// ensure we are at the security settings tab
				if (this.$route.fullPath !== '/account_settings/?tab=security') {
					this.$router.push({ path: '/account_settings/?tab=security' })
				}
			},
			handleClose() {
				// bubble the close event upwards
				this.recoverySaveAction = ''
				this.resetWarningsAndErrors()
				this.$emit('close')

				// if we are coming from the enforced MFA, trigger logout.
				if (
					this.flowStageStart !== CONSTANTS.MFA.FLOW_STAGES.RESTORE_CONFIGURATION &&
					this.$route.fullPath !== '/account_settings/?tab=security'
				) {
					this.logout()
				}
			},
			async logout() {
				$('body').removeClass('menu-active')
				$('#mfa-configuration-modal').modal('hide')
				clearInterval(this.t)
				await this.$store.dispatch('Auth/logout')
				this.$router.push({ name: 'login_page' }).catch(() => {})
			},
			resetWarningsAndErrors() {
				this.ajaxError.errors = []
				this.ajaxWarnings = {}
			},
			validateConfigOTP(code) {
				this.resetWarningsAndErrors()
				this.stateChangeKey += 1
				let formStatus = true
				const errorObject = {
					status: 'error',
					message: this.$t('The given data was invalid.'),
					errors: {},
				}
				this.stateChangeKey += 1

				if (!helper.validateMFACodeFormat(code?.trim())) {
					Object.assign(errorObject.errors, {
						otp: ['Invalid code format. Please enter a valid 6-digit code.'],
					})
					formStatus = false
				}

				Object.assign(this.ajaxError, errorObject)

				if (formStatus) {
					const otpPayload = {
						totp: code?.trim(),
						ttl_days: CONSTANTS.MFA.DEFAULT_TTL_DAYS, // maximum ttl days
						ttl_start: Math.floor(Date.now() / 1000),
						name: this.authenticatorName.trim(), // Add the name to payload
					}
					axios
						.post(helper.removePrefix(this.nextRoute, '/api/'), otpPayload, {
							baseURL: configSettings.authApiUrl,
						})
						.then(response => {
							this.recovery = response.data.recovery_text
							this.configId = response.data.config_id
							this.showRecoveryText()
						})
						.catch(error => {
							this.$logError(error.response.data.message)
							if (error.response.status === 400) {
								Object.assign(errorObject.errors, {
									otp: ['Please enter a valid 6-digit code.'],
								})
							} else {
								Object.assign(errorObject.errors, {
									otp: [this.$t(error.response.data.message)],
								})
							}
							this.ajaxError = { ...errorObject }
							this.stateChangeKey += 1
						})
						.finally(() => {
							this.loading = false
						})
				}
			},
			async copyToClipboard(type) {
				let text = ''
				if (type === 'recovery') {
					this.recoverySaveAction = 'Copied'
					text = this.recovery
				} else {
					text = this.secret
				}
				return helper.copyToClipboard(text, () => {
					this.$stanNotify({
						type: 'success',
						text: this.$t(`${type === 'recovery' ? 'Backup codes' : 'Setup key'} copied!`),
					})
				})
			},
			downloadRecoveryCodes() {
				const element = document.createElement('a')
				const file = new Blob([this.recovery], { type: 'text/plain' })
				element.href = URL.createObjectURL(file)
				element.download = 'stan-recovery-codes.txt'
				document.body.appendChild(element)
				element.click()
				document.body.removeChild(element)
				this.recoverySaveAction = 'Downloaded'
			},
		},
	}
</script>

<style lang="scss" scoped>
	.mfa-config-modal {
		max-height: 100vh;
		/* If dvh is supported, apply it */
		@supports (max-height: 100dvh) {
			max-height: 100dvh;
		}
	}

	.font-regular {
		font-weight: 400;
	}
	.font-semibold {
		font-weight: 600;
	}
	.mfa-modal-header {
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
		line-height: normal;
		color: var(--stan-text-dark-color);

		.modal-pretext {
			margin: 5px;
			align-self: stretch;
		}
	}
	.mfa-title-icon {
		width: 50px;
		height: 50px;
		margin-bottom: 16px;
	}
	.inner-pretext {
		font-size: small;
		margin-block-end: 0%;
	}

	#mfa-method-choice {
		p {
			font-family: 'Inter', sans !important;
		}
		.mfa-choice-container {
			display: flex;
			flex-direction: column;
			gap: 16px;
		}
		.mfa-choice-btn {
			color: var(--stan-text-dark-color) !important;
			border: 1.5px solid var(--stan-text-light-color) !important;
			border-radius: 12px !important;
			padding: 0 !important;
			box-shadow: none !important;
			transition: border 0.2s;
			display: flex;
			justify-content: flex-start;
			text-align: left;
		}

		.mfa-choice-row {
			display: flex;
			align-items: center;
			gap: 16px;
			padding: 20px 24px;
			width: 100%;
		}
		.mfa-choice-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 40px;
			height: 40px;
			margin-right: 8px;
		}
	}

	#mfa-qr-data {
		.setup-code {
			padding: 1rem 1.5rem 1.5rem 1.5rem;
		}

		.secret {
			color: var(--stan-primary-primary-color);
			font-size: 1.5rem;
			font-weight: 600;
			margin: 0 auto;
			padding: 0.5rem;
			word-break: break-all;
			white-space: normal;
			overflow-wrap: break-word;
			width: 90%;
		}

		.form-group .input {
			background-color: var(--stan-gray-light-color) !important;
		}
	}

	#mfa-recovery-codes {
		.recovery-card {
			font-family: 'Inter';
			font-style: normal;
			position: relative;
			display: flex;
			flex-direction: column;
			justify-content: center;
			padding-top: 10px;
			width: 100%;
			height: 200px;
			background: var(--stan-gray-light-color);
			border-radius: 15px;
			text-align: center;
			margin-bottom: 12px;
			line-height: 100%;

			.copy-icon {
				position: absolute;
				top: 0.5rem;
				right: 0.5rem;
				width: 24px;
				height: 24px;
				cursor: pointer;
				transition: opacity 0.2s;
				opacity: 0.7;
			}
			.copy-icon:hover {
				opacity: 1;
			}

			.recovery-text {
				font-weight: 400;
				font-size: 24px;
				line-height: 2rem;
				align-self: stretch;
				color: var(--stan-text-dark-color);
				width: 65%;
				margin: 0 auto;
			}
		}
	}

	.recovery-save-action-card {
		font-family: 'Inter';
		font-style: normal;
		position: relative;
		display: flex;
		flex-direction: row;
		justify-content: center;
		width: 100%;
		height: 50px;
		background: #e4fff9;
		border-radius: 15px;
		text-align: center;
		margin-bottom: 12px;
		line-height: 100%;
		align-items: center;
		gap: 10px;
	}

	.check-mark-icon {
		width: 24px;
		height: 24px;
		margin-left: 10px;
		fill: var(--stan-green-3);
	}
	.check-mark-icon::v-deep path {
		fill: var(--stan-green-3);
		color: var(--stan-green-3);
	}

	.mfa-complete-buttons {
		width: 46%;
		margin: 1.5rem;
	}

	.action-text {
		color: var(--stan-green-3);
	}

	.action-button-group {
		display: flex;
		flex-direction: column;
		row-gap: 2px;
	}
</style>
